/*
 * @Author: Cozy
 * @Date: 2024-05-09 17:10:04
 * @LastEditors: Cozy
 * @LastEditTime: 2024-07-26 19:17:43
 * @FilePath: /SChatUI/src/ChatPage.js
 * @Description: 
 * 
 * Copyright (c) 2024 by Cozy, All Rights Reserved. 
 */
// ChatPage.js  
import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Chat, { Bubble, useMessages, RichText, Divider, Popup, RadioGroup, Button, Text, Input, toast } from "@chatui/core";
import { v4 as uuidv4 } from 'uuid';
import io from 'socket.io-client';
import "@chatui/core/dist/index.css";
import { AuthContext } from './AuthContext';
import DOMPurify from 'dompurify';

export default function ChatPage() {
    const { service, sessionId } = useParams();
    const { messages, appendMsg, updateMsg, setTyping } = useMessages([]);
    const { user, clearAuth, accessToken, refreshAttempts, accessibleServices } = useContext(AuthContext);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();
    const [socket, setSocket] = useState(null);
    const [isTranslateEnabled, setisTranslateEnabled] = useState(false);
    const [isQuickRepliesVisible, setisQuickRepliesVisible] = useState(true);
    const [value3, setValue3] = useState('');
    const [supportedLanguages, setSupportedLanguages] = useState([
        { label: '中文', value: 'Chinese' },
        { label: 'Español', value: 'Spanish' },
        { label: 'English', value: 'English' },
    ]);

    const chatInstanceRef = useRef(null);
    // 注意此处是多语种适配翻译，而不是翻译语言，支持的翻译语言随配置下发
    const translations = {
        en: 'Translate',
        zh: '翻译',
        es: 'Traducir',
        ja: '翻訳',
        ko: '플랫폼',
        fr: 'Traduire'
    };
    const userLanguage = navigator.language.slice(0, 2); // 获取用户语言代码
    const translateText = translations[userLanguage] || translations.en; // 根据用户语言选择翻译文本 
    function handleUseTranslatedText() {

        if (chatInstanceRef.current) {
            console.log("chatInstanceRef.current:", chatInstanceRef);
            chatInstanceRef.current.setText(translatedInput);
            // chatInstanceRef.current.setInputValue(translatedInput);
        }
        handleClose();

    }
    const defaultQuickReplies = [
        {
            name: `🌐 ${translateText}`,
            isHighlight: true,
        },
        // {
        //   name: '短语1',
        //   isNew: true,
        // },
        // {
        //   name: '短语2',
        //   isHighlight: true,
        // },
        // {
        //   name: '短语3',
        // },
    ];

    useEffect(() => {
        if (!accessToken || refreshAttempts >= 3) {
            console.warn("Missing or invalid access token");
            clearAuth();
            window.location.href = "/login";
            return;
        }

        const socket = io(process.env.REACT_APP_BASE_URL, {
            transports: ['websocket'],
            withCredentials: true,
            auth: {
                token: accessToken
            },
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            randomizationFactor: 0.5
        });

        setSocket(socket);

        socket.on('invalid_token', () => {
            console.error('invalid_token');
            clearAuth();
        });

        socket.emit('join', {
            service: service,
            session_id: sessionId,
            token: accessToken,
            user_name: user
        });



        socket.on('load_history', (history) => {
            history.forEach((msg) =>
                appendMsg({
                    _id: msg.message_id,
                    type: "text",
                    content: { text: msg.message, tag: msg.tag, language: msg.language, translated_text: msg.translated_text },
                    position: msg.sender === "user" ? "right" : "left",
                    createdAt: new Date(msg.timestamp).toLocaleString()
                })
            );
        });

        socket.on('receive_message', (data) => {
            appendMsg({
                _id: data.message_id,
                type: "text",
                content: { text: data.message, tag: "neutral" },
                position: "left",
            });
            setTyping(false);
        });
        socket.on('update_message', (data) => {
            console.debug("update_message:", data);
            updateMsg(data.message_id, {
                type: "text",
                content: { text: data.message },
                position: "left",
            });
        });
        socket.on('update_translated_message', (data) => {
            console.debug("update_translated_message:", data);
            if (data && data.status === "success") {
                if (data.message_id === "MsgID_input_text") {
                    setTranslatedInput(data.translated_text);
                    return;
                }
                updateMsg(data.message_id, {
                    type: "text",
                    content: { text: data.message, tag: data.tag, language: data.language, translated_text: data.translated_text },
                    position: "left"
                });
            } else if (data && data.status === "error") {
                const originalMessage = messages[data.message_id];
                updateMsg(data.message_id, {
                    ...originalMessage,
                    content: { ...originalMessage.content, language: "" },
                });
            }

        });
        socket.on('page_config', (data) => {
            console.debug("page_config:", data);
            setisTranslateEnabled(data.isTranslateEnabled);  

            if (data.isTranslateEnabled && data.supportedLanguages && (data.supportedLanguages.length > 0)) {
                setSupportedLanguages(data.supportedLanguages);
            }

        });

        socket.on('test', (data) => {
            console.debug("test:", data);
        });

        socket.on('connect_error', (err) => {
            console.debug('连接错误:', err.message);
        });

        socket.on('connect', () => {
            console.info('连接成功');
            console.debug('Socket:', socket);
        });

        socket.on('disconnect', () => {
            console.debug('断开连接', socket);
            if (socket.receiveBuffer.toString().includes("invalid_token")) {
                console.info("clear auth");
                clearAuth();
            } else {
                console.debug("[disconnect]", socket);
            }
        });
        return () => {
            socket.disconnect();
        };
    }, [service, sessionId, appendMsg, setTyping, clearAuth, refreshAttempts, accessToken]);

    const updateMessageTag = (id, tag) => {
        const messageIndex = messages.findIndex((msg) => msg._id === id);
        if (messageIndex === -1) return;

        const originalMessage = messages[messageIndex];
        const updatedMessage = {
            ...originalMessage,
            content: { ...originalMessage.content, tag },
        };

        updateMsg(id, updatedMessage);

        socket.emit('update_tag', {
            service: service,
            session_id: sessionId,
            message: updatedMessage.content.text,
            tag,
            message_id: id
        });
    };
    const translateTo = (message_id, message, target_language) => {
        socket.emit('translate', {
            service: service,
            message_id: message_id,
            message: message,
            target_language: target_language
        });
    }

    function renderMessageContent(msg) {
        const { content, _id } = msg;
        const [islike, setislikeValue] = useState(content.tag);
        const [language, setlanguageValue] = useState(content.language);
        const options = [
            { label: '👍', value: 'like' },
            { label: '👎', value: 'dislike' }
        ];
        function handleChangeisLike(val) {
            setislikeValue(val);
            updateMessageTag(_id, val)
        }

        function handleChangeLanguage(val) {
            setlanguageValue(val);
            translateTo(_id, content.text, val);
        }

        return (
            <div>
                {msg.position === "right" && (
                    <Bubble>
                        <RichText content={content.text} />
                    </Bubble>
                )}
                {msg.position === "left" && (
                    <div>
                        {isTranslateEnabled && content.translated_text ? (
                            <Bubble>
                                <RichText content={content.text} />
                                <Divider />
                                <RichText content={content.translated_text} />
                            </Bubble>
                        ) : (
                            <Bubble>
                                <RichText content={content.text} />
                            </Bubble>
                        )}
                        <div style={{ display: "flex", marginTop: "5px" }}>
                            <RadioGroup value={islike} options={options} onChange={handleChangeisLike} />
                            {isTranslateEnabled && (
                                <RadioGroup value={language} options={supportedLanguages} onChange={handleChangeLanguage} />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    }
    function handleOpen() {
        setOpen(true);
    }

    function handleClose() {
        setOpen(false);
    }


    function handleSend(type, val) {
        const MAX_MESSAGE_LENGTH = 200;
        const cleanVal = DOMPurify.sanitize(val);
        if (type === "text") {
            if (!cleanVal.trim()) {
                return;
            }

            if (cleanVal.length > MAX_MESSAGE_LENGTH) {
                toast.fail("消息过长，请缩短后再发送。");
                return;
            }

            const newMessageId = uuidv4();
            appendMsg({
                _id: newMessageId,
                type: "text",
                content: { text: cleanVal },
                position: "right"
            });

            setTyping(true);

            socket.emit('send_message', {
                service: service,
                message: cleanVal,
                session_id: sessionId,
                token: accessToken,
                user_name: user,
                send_time: Date.now(),
                message_id: newMessageId
            });

        }
    }

    function handleQuickReplyClick(item) {
        handleOpen();
    }
    const goBack = () => {
        window.history.back();
    };
    const [translatedInput, setTranslatedInput] = useState('');
    const [language, setlanguageValue] = useState('');
    const languages = supportedLanguages;

    function handleChangeLanguage(val) {
        setlanguageValue(val);
        translateTo("MsgID_input_text", value3, val);
    }

    return (
        <div className="ChatApp">
            <Chat
                navbar={{
                    title: sessionId,
                    leftContent: {
                        label: "<",
                        size: "md",
                        onClick: goBack,
                    },
                    rightContent: [],
                }}
                messages={messages}
                renderMessageContent={(msg) => renderMessageContent(msg)}
                onSend={handleSend}
                quickRepliesVisible={isTranslateEnabled}
                quickReplies={defaultQuickReplies}
                onQuickReplyClick={handleQuickReplyClick}
                composerRef={chatInstanceRef}
            />
            <Popup
                active={open}
                title={translateText}
                onClose={handleClose}

            >
                <div style={{ padding: '1px var(--gutter)' }}>
                    <div style={{
                        display: "flex",
                        padding: '0px 5px',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <div>
                            <RadioGroup
                                value={language}
                                options={supportedLanguages}
                                onChange={handleChangeLanguage}
                                disabled={value3 === ""}
                            />
                        </div>
                        <Button
                            style={{ margin: '0px 5px 5px 0px' }}
                            onClick={handleUseTranslatedText}
                            disabled={translatedInput === ""}
                            color="primary"
                        >使用</Button>
                    </div>
                    {translatedInput === "" ? null : (<Divider />)}
                    <div >
                        <div style={{ maxHeight: '7em', overflowY: 'auto' }}>
                            <Text style={{ padding: '0px 6px var(--gutter)' }}>{translatedInput}</Text>
                        </div>
                        <div>
                            <Input
                                autoSize
                                value={value3}
                                onChange={val => setValue3(val)}
                                maxLength={200}
                                placeholder="请输入..."
                            />
                        </div>
                    </div>
                </div>
            </Popup>
        </div>


    );
}
